import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {useAgencyPackActivity} from '../../hooks/useAgencyPackActivity.ts'
import {useAgencyActivity} from '../../hooks/useAgencyActivity.ts'
import {INVITATION_CODES_QUERY_KEY, useInvitationCodesByAgency} from '../../hooks/useInvitationCodes.ts'
import {PackActivityTables} from './PackActivityTables.tsx'
import {AgencyActivityCard} from './AgencyActivityCard.tsx'
import {AgencySelfServiceCodeSections} from './AgencySelfServiceCodeSections.tsx'
import {redemptionUrlPrefix} from '../../utils/redemptionUrl.ts'
import {sumActivationCounters} from '../activation/activationView.ts'
import IconCopy from '../../assets/icons/copy.svg?react'
import IconCheck from '../../assets/icons/check.svg?react'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconCopyLink from '../../assets/icons/copy-link.svg?react'

interface PacksTripbooksSectionProps {
    agencyId: string
    genericCode?: string
    primaryMarket?: string
    isSelfService?: boolean
    // Admin looking at one agency's detail page, rendered exactly like that
    // agency's own self-service view (AgencySelfServiceCodeSections) instead of
    // the internal tourOperator layout — display only. Kept separate from
    // isSelfService because that flag also picks the API path (/me/... vs
    // /agencies/{id}/...), and an admin has no "/me" agency to read from.
    viewAsAgency?: boolean
    // The agency's TripBook pages — what a pack without its own pagination uses.
    numberOfPagesIncluded?: number
    // Opens the "No activados" page (agency dashboard path). Optional: only
    // the agency's own dashboard has it.
    onOpenNotActivated?: () => void
}

export const PacksTripbooksSection: React.FC<PacksTripbooksSectionProps> = ({agencyId, genericCode, primaryMarket, isSelfService, viewAsAgency, numberOfPagesIncluded, onOpenNotActivated}) => {
    const {t} = useTranslation('dashboard')
    const showAgencyView = isSelfService || viewAsAgency
    const {isLoading, isPayPerUse, activity, genericCodes, genericCodeIds, printed, created, inactive, packages, packs} =
        useAgencyPackActivity(agencyId, genericCode, isSelfService)
    // Self-service agencies now get one unified code view (AgencySelfServiceCodeSections)
    // regardless of pack format — pay-per-use ones don't have packs to pool codes
    // from, so their printed/created/inactive come from the agency-wide activity
    // endpoint instead (same source AgencyActivityCard uses for the internal view).
    const {activity: payPerUseActivity, isLoadingActivity: isLoadingPayPerUseActivity} =
        useAgencyActivity(agencyId, primaryMarket, !!(showAgencyView && isPayPerUse), isSelfService)
    const {invitationCodes} = useInvitationCodesByAgency(agencyId)
    const queryClient = useQueryClient()
    const [copiedId, setCopiedId] = useState<string | null>(null)

    // Memoised: the code tables key their row overlay off this map, so a new
    // reference on every render would recompute every row list each time.
    const invitationCodesById = useMemo(
        () => new Map(invitationCodes.map((record) => [record.id, record])),
        [invitationCodes]
    )

    const linkPrefix = redemptionUrlPrefix(primaryMarket)

    // TripBook Activation: backend counters for the pooled header (null when
    // any pack predates them), the pack each code belongs to (sent as packId
    // on assignment), and the pack to charge when a new code is minted.
    const activationCounters = useMemo(() => sumActivationCounters(packs.map((p) => p.pack.activationCounters)), [packs])
    const packIdByCodeId = useMemo(() => {
        const map = new Map<string, string>()
        for (const p of packs) {
            for (const row of [...p.inactive, ...p.created, ...p.printed]) {
                if (row.codeId) map.set(row.codeId, p.pack.id)
            }
        }
        return map
    }, [packs])
    const defaultPackId = useMemo(() => [...packs]
        .sort((a, b) => a.pack.purchasedAt.localeCompare(b.pack.purchasedAt))
        .find((p) => (p.pack.activationCounters?.available ?? 0) > 0)?.pack.id, [packs])

    const onCodeCreated = () => {
        queryClient.invalidateQueries({queryKey: ['b2b-pack-detail', agencyId]})
        queryClient.invalidateQueries({queryKey: ['b2b-agency-activity', agencyId]})
        queryClient.invalidateQueries({queryKey: [INVITATION_CODES_QUERY_KEY, agencyId]})
    }

    if (isLoading || (showAgencyView && isPayPerUse && isLoadingPayPerUseActivity)) {
        return (
            <div className="flex items-center justify-center py-16">
                <IconSpinner className="size-8"/>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5  pb-10">

            {/* Stats (KPI cards) live in the "Estadísticas" tab instead
                (AgencyStatsSection) — the self-service "Packs y TripBooks" tab is
                code tables only, per the (Vista agencia) design. */}

            {/* Internal (tourOperator staff) view only — the agency-style view
                (self-service login or admin's viewAsAgency) omits its own
                invitation link here too, per the same design. */}
            {!showAgencyView && genericCodes.length > 0 && (
                <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-[22px] flex flex-col gap-4">
                    <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">
                        {genericCodes.length > 1 ? t('packs.invitationLinksTitlePlural') : t('packs.invitationLinksTitleSingle')}
                    </span>
                    {genericCodes.map((g) => {
                        const link = `${linkPrefix}${g.code}`
                        return (
                            <div key={g.id} className="flex items-center gap-3.5 flex-wrap">
                                <span className="flex-1 min-w-[260px] flex items-center gap-3 bg-neutralBlack-50 border border-neutralBlack-200 rounded-2xl px-[18px] py-3.5">
                                    <IconCopyLink className="size-5 flex-none text-neutralBlack-500"/>
                                    <span className="flex-1 min-w-0 text-[19px] tracking-tight truncate">
                                        <span className="text-neutralBlack-500">{linkPrefix}</span>
                                        <span className="font-semibold text-neutralBlack-900">{g.code}</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 bg-neutralBlack-100 rounded-full px-2.5 py-1 text-xs text-neutralBlack-900 whitespace-nowrap">
                                        {g.isPaid ? t('packs.paidLink') : t('packs.genericCodeBadge')}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard.writeText(link).then(() => {
                                            setCopiedId(g.id)
                                            setTimeout(() => setCopiedId(null), 1600)
                                        })
                                    }}
                                    className={`inline-flex items-center gap-2 h-12 px-5 rounded-full text-white text-sm font-medium whitespace-nowrap ${copiedId === g.id ? 'bg-success' : 'bg-neutralBlack-900 hover:bg-neutralBlack-700'}`}
                                >
                                    {copiedId === g.id ? <IconCheck className="size-3.5"/> : <IconCopy className="size-3.5"/>}
                                    {copiedId === g.id ? t('packs.linkCopied') : t('packs.copyLink')}
                                </button>
                            </div>
                        )
                    })}
                    <p className="text-[13px] leading-relaxed text-neutralBlack-500 max-w-[640px]">
                        {isPayPerUse ? t('packs.invitationHelpPayPerUse') : t('packs.invitationHelpPackaged')}
                    </p>
                </section>
            )}

            {showAgencyView ? (
                <AgencySelfServiceCodeSections
                    agencyId={agencyId}
                    printed={isPayPerUse ? (payPerUseActivity?.printed ?? []) : printed}
                    created={isPayPerUse ? (payPerUseActivity?.created ?? []) : created}
                    inactive={isPayPerUse ? (payPerUseActivity?.inactive ?? []) : inactive}
                    primaryMarket={primaryMarket}
                    onCodeCreated={onCodeCreated}
                    invitationCodesById={invitationCodesById}
                    excludeCodeIds={genericCodeIds}
                    totalUnits={activity.totalUnits}
                    hasPacks={!isPayPerUse}
                    // An agency viewing its own self-service tables never gets to
                    // add/edit feedback (staff-write-only) — only the admin looking
                    // at this same view through viewAsAgency does.
                    canEditFeedback={!!viewAsAgency}
                    // Same admin-only widening for "cancelar código": an agency can
                    // only cancel a code pending delivery; the admin can also
                    // cancel one already delivered or with a TripBook created.
                    canCancelBeyondPending={!!viewAsAgency}
                    canHideTrips={!!viewAsAgency}
                    packages={packages}
                    agencyPages={numberOfPagesIncluded ?? 60}
                    activationCounters={isPayPerUse ? null : activationCounters}
                    packIdByCodeId={packIdByCodeId}
                    defaultPackId={defaultPackId}
                    isStaffView={!!viewAsAgency}
                    onOpenNotActivated={onOpenNotActivated}
                />
            ) : isPayPerUse ? (
                <AgencyActivityCard
                    agencyId={agencyId}
                    primaryMarket={primaryMarket}
                    onCodeCreated={onCodeCreated}
                    invitationCodesById={invitationCodesById}
                    isSelfService={isSelfService}
                    excludeCodeIds={genericCodeIds}
                />
            ) : (
                <PackActivityTables
                    agencyId={agencyId}
                    printed={printed}
                    created={created}
                    inactive={inactive}
                    primaryMarket={primaryMarket}
                    onCodeCreated={onCodeCreated}
                    invitationCodesById={invitationCodesById}
                    excludeCodeIds={genericCodeIds}
                    totalUnits={activity.totalUnits}
                    packages={packages}
                    agencyPages={numberOfPagesIncluded ?? 60}
                />
            )}
        </div>
    )
}
